import { supabase } from '../supabaseClient';
import { complianceChaseEmail, sendEmail } from './email';

// Chase = get the supplier to act on outstanding compliance (expired /
// rejected / never-sent). Every "needs you" state that resolves by the
// supplier doing something, not by a review decision (compliance-queue §5).
// One email per supplier listing everything outstanding — never one per
// document — rate-limited to once per supplier per 7 days.

export const CHASE_COOLDOWN_DAYS = 7;

// Last time each supplier was chased. Reuses the existing valid audit action
// 'reminder_sent' with meta.type='chase', so no migration is needed (the
// action CHECK already permits 'reminder_sent'). Returns Map<supplier_id, ISO>.
export async function fetchLastChasedMap(supplierIds = []) {
  const map = new Map();
  if (!supplierIds.length) return map;
  const { data } = await supabase
    .from('compliance_audit_log')
    .select('supplier_id, ts, meta')
    .in('supplier_id', supplierIds)
    .eq('action', 'reminder_sent')
    .order('ts', { ascending: false });
  for (const r of data || []) {
    if (r.meta?.type !== 'chase') continue;      // ignore per-document expiry reminders
    if (!map.has(r.supplier_id)) map.set(r.supplier_id, r.ts); // newest wins (ordered desc)
  }
  return map;
}

// Milliseconds left on the cooldown for a supplier (0 = chaseable now).
export function chaseCooldownRemaining(lastChasedIso, now = Date.now()) {
  if (!lastChasedIso) return 0;
  const elapsed = now - new Date(lastChasedIso).getTime();
  return Math.max(0, CHASE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000 - elapsed);
}

// Human "chased 3 days ago" / "chased today".
export function formatLastChased(lastChasedIso, now = Date.now()) {
  if (!lastChasedIso) return null;
  const days = Math.floor((now - new Date(lastChasedIso).getTime()) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'chased today';
  if (days === 1) return 'chased yesterday';
  return `chased ${days} days ago`;
}

// Send ONE chase email to a supplier's active portal users listing the given
// outstanding documents, then log to supplier_email_log + compliance_audit_log
// (action 'reminder_sent', meta.type='chase'). Callers enforce the cooldown
// via fetchLastChasedMap; this refuses (skipped:'no_recipients') when the
// supplier has no active portal user with an email.
export async function chaseSupplier({ supplierId, supplierName, documents, actorId, portalOrigin }) {
  if (!supplierId || !documents?.length) return { emailed: 0, skipped: 'nothing' };
  const { data: users } = await supabase
    .from('supplier_users')
    .select('email, full_name')
    .eq('supplier_id', supplierId)
    .eq('is_active', true);
  const recipients = (users || []).filter(u => u.email);
  if (!recipients.length) return { emailed: 0, skipped: 'no_recipients' };

  const portalUrl = `${portalOrigin || ''}/portal/compliance`;
  const sentTo = [];
  for (const u of recipients) {
    const payload = complianceChaseEmail({ contactName: u.full_name, supplierName, documents, portalUrl });
    try { await sendEmail({ to: u.email, subject: payload.subject, html: payload.html }); sentTo.push(u.email); }
    catch (e) { /* best-effort per recipient */ }
  }
  if (sentTo.length > 0) {
    await supabase.from('supplier_email_log').insert([
      { supplier_id: supplierId, email_kind: 'compliance_chase', recipients: sentTo, sent_by: actorId || null },
    ]);
    await supabase.from('compliance_audit_log').insert([
      {
        action: 'reminder_sent',
        supplier_id: supplierId,
        document_key: null,
        actor_id: actorId || null,
        meta: { type: 'chase', document_keys: documents.map(d => d.document_key).filter(Boolean), count: documents.length },
      },
    ]);
  }
  return { emailed: sentTo.length };
}
